
import { useState, useImperativeHandle, forwardRef } from 'react';
import { toast } from 'sonner';
import { DocusealForm } from '@docuseal/react';
import { useCreateDocuSealSubmission } from '@/hooks/useCreateDocuSealSubmission';
import { useReferentBrandingColors } from '@/hooks/useReferentBrandingColors';

interface BrandingSettings {
  enabled: boolean;
  logo_url?: string;
  primary_color?: string;
}

interface SignatureStepProps {
  referentData?: {
    id: string;
    name: string;
    email: string;
  };
  campaignId?: string;
  updateFormData?: (data: any) => void;
  branding?: BrandingSettings | null;
  onSignatureComplete?: () => void;
}

interface SignatureStepRef {
  handleSign: () => Promise<void>;
  isSignaturePending: boolean;
}

export const SignatureStep = forwardRef<SignatureStepRef, SignatureStepProps>(({ referentData, campaignId, updateFormData, branding, onSignatureComplete }, ref) => {
  const [embeddingUrl, setEmbeddingUrl] = useState<string | null>(null);
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const createSubmission = useCreateDocuSealSubmission();
  const colors = useReferentBrandingColors(branding);

  const handleSign = async () => {
    if (!referentData || !campaignId) {
      toast.error('Erreur', {
        description: 'Données manquantes pour la signature',
      });
      return;
    }
    
    try {
      const result = await createSubmission.mutateAsync({
        referentData,
        campaignId
      });
      
      setEmbeddingUrl(result.embeddingUrl);
      setSubmissionId(result.submissionId);
      
      toast.success('Document prêt pour signature', {
        description: 'Veuillez signer le document ci-dessous.',
      });
    } catch (_error) {
      // L'erreur est déjà gérée dans le hook
    }
  };

  // Exposer la fonction handleSign au parent via ref
  useImperativeHandle(ref, () => ({
    handleSign,
    isSignaturePending: createSubmission.isPending
  }));

  const handleDocumentComplete = () => {
    // Mettre à jour formData avec la signature
    if (updateFormData) {
      const signatureData = {
        signedDocumentUrl: submissionId || 'completed'
      };
      updateFormData(signatureData);
    }
    
    toast.success('Charte signée avec succès !', {
      description: 'Passage à l\'étape suivante...'
    });

    // Navigation immédiate vers l'étape suivante après signature
    if (onSignatureComplete) {
      onSignatureComplete();
    }
  };

  // Plus d'écran de succès - navigation immédiate

  return (
    <div className="space-y-6 sm:space-y-8 font-['Bricolage_Grotesque'] px-4 sm:px-0">
      {/* Titre principal centré */}
      <div className="text-center">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 font-['Bricolage_Grotesque'] leading-tight">
          Signature électronique de la charte
        </h1>

      </div>

      {!embeddingUrl ? (
        <>
          {/* Section Signature requise avec barre colorée */}
          <div 
            className="bg-gray-50 border-l-4 p-4 sm:p-6 rounded-r-lg"
            style={{ borderLeftColor: colors.primary }}
          >
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
              Signature requise
            </h2>
            <div className="space-y-3 text-sm sm:text-base text-gray-600">
              <p>
                Avant de procéder à l'évaluation, veuillez signer notre charte de véracité.
              </p>
              <p>
                Cette signature confirme votre engagement à fournir des informations 
                exactes et actualisées lors de votre évaluation.
              </p>
            </div>
          </div>


        </>
      ) : (
        <div className="space-y-4 sm:space-y-6">
          <div 
            className="bg-gray-50 border-l-4 p-4 sm:p-6 rounded-r-lg"
            style={{ borderLeftColor: colors.primary }}
          >
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
              Signature de la charte de véracité
            </h2>
            <p className="text-sm sm:text-base text-gray-600">
              Veuillez lire et signer la charte ci-dessous pour confirmer 
              votre engagement à fournir des informations véridiques.
            </p>
          </div>
          
          <DocusealForm
            src={embeddingUrl}
            className="w-full h-[500px] sm:h-[600px] border rounded-lg"
            onComplete={handleDocumentComplete}
            data-hide-download-button="true"
            data-hide-send-copy-button="true"
            data-auto-close-on-complete="true"
          />
        </div>
      )}
    </div>
  );
});
